import * as React from 'react';

import { Icon } from './Icon';
import { Text } from './Typography';

// Closed primitive — single <Breadcrumb items maxItems? />. The path is
// pure data; the only display choice is whether (and how aggressively)
// to compact long trails.
//
//   <Breadcrumb items={[
//       { label: 'Components', href: '/design_system' },
//       { label: 'Card', href: '/design_system#/components/card' },
//       { label: 'Variants' }                       // current page
//   ]} />
//
// An item without `href` renders as the current page (`aria-current`).
// Typically the last entry, but not enforced — whatever the data says.
//
// `maxItems` caps the visible count. When `items.length > maxItems` the
// trail compacts to `[items[0..left], …, items[length-right..]]` where
// `right = ceil(maxItems/2)` and `left = floor(maxItems/2)`. Examples
// for a 5-item trail: maxItems=2 → 1+1, =3 → 1+2, =4 → 2+2, =5 →
// nothing to compact. Clicking the ellipsis flips internal state and
// the full trail expands inline. Without `maxItems`, every item is
// always visible.

const Breadcrumb = React.forwardRef(({
    className: _className,
    items = [],
    maxItems,
    style: _style,
    ...rest
}, ref) => {
    if (maxItems !== undefined && maxItems < 2) {
        throw new Error(`Breadcrumb: maxItems must be at least 2 (received ${maxItems}). Below 2 there is no meaningful split between the left and right segments.`);
    }

    const [expanded, setExpanded] = React.useState(false);

    // When maxItems is at least the number of items, there is nothing to
    // compact — drop the constraint up front so the rest of the function
    // reads as "compact iff activeMaxItems is set and we're not expanded".
    const activeMaxItems = (maxItems !== undefined && maxItems < items.length)
        ? maxItems
        : undefined;
    const shouldCompact = activeMaxItems !== undefined && !expanded;

    let leadingItems;
    let trailingItems;

    if (shouldCompact) {
        const rightCount = Math.ceil(activeMaxItems / 2);
        const leftCount = activeMaxItems - rightCount;

        leadingItems = items.slice(0, leftCount);
        trailingItems = items.slice(items.length - rightCount);
    } else {
        leadingItems = items;
        trailingItems = [];
    }

    const renderItem = (item, isLast) => (
        <li className="tw-inline-flex tw-items-center tw-gap-2">
            {item.href ? (
                <a
                    href={item.href}
                    className="tw-text-muted-foreground tw-transition-colors hover:tw-text-primary"
                >
                    <Text tone="inherit" variant="control">{item.label}</Text>
                </a>
            ) : (
                <span aria-current={isLast ? 'page' : undefined} className="tw-text-foreground">
                    <Text tone="inherit" variant="control">{item.label}</Text>
                </span>
            )}
        </li>
    );

    const renderSeparator = (key) => (
        <li key={key} role="presentation" aria-hidden="true" className="tw-text-muted-foreground">
            <Icon icon="chevronRight" size="small" tone="inherit" />
        </li>
    );

    const segments = [];

    leadingItems.forEach((item, index) => {
        const globalIndex = index;
        const isLast = !shouldCompact && index === items.length - 1;

        segments.push(
            <React.Fragment key={`leading-${globalIndex}-${item.label}`}>
                {renderItem(item, isLast)}
                {(!isLast || shouldCompact) ? renderSeparator(`leading-sep-${globalIndex}`) : null}
            </React.Fragment>
        );
    });

    if (shouldCompact) {
        segments.push(
            <li key="ellipsis">
                <button
                    type="button"
                    aria-label="Show hidden steps"
                    className="tw-flex tw-items-center tw-text-muted-foreground tw-transition-colors hover:tw-text-primary"
                    onClick={() => setExpanded(true)}
                >
                    <Icon icon="ellipsis" size="small" tone="inherit" />
                </button>
            </li>
        );
        segments.push(renderSeparator('ellipsis-sep'));

        trailingItems.forEach((item, index) => {
            const globalIndex = items.length - trailingItems.length + index;
            const isLast = index === trailingItems.length - 1;

            segments.push(
                <React.Fragment key={`trailing-${globalIndex}-${item.label}`}>
                    {renderItem(item, isLast)}
                    {!isLast ? renderSeparator(`trailing-sep-${globalIndex}`) : null}
                </React.Fragment>
            );
        });
    }

    return (
        <nav ref={ref} aria-label="breadcrumb" {...rest}>
            <ol className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-break-words tw-text-muted-foreground sm:tw-gap-2.5">
                {segments}
            </ol>
        </nav>
    );
});

Breadcrumb.displayName = 'Breadcrumb';

export { Breadcrumb };
